import {ColDef, ICellRendererParams, RowNode} from 'ag-grid-community';
import {
  arr_reduce,
  blank,
  data_get,
  data_is_true,
  data_nullify,
  fallback,
  OptionalValue,
  spf,
  is_callable,
  when,
  to_local_datetime,
  filled,
  arr_filter,
  arr_count,
  arr_first,
} from '../../utils/common';
import {
  ICellRendererAngularComp,
} from 'ag-grid-angular';
import {Component} from '@angular/core';
import {to_numeric} from '../../utils/numbers';
import {FieldmagicPillVariant} from '../common/pill.component';
import {BehaviorSubject} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FieldmagicTextPurpose } from '../text/text.component';

export type FieldmagicDataGridRenderingConfig = {
  placeholder?: string | ((params: ICellRendererParams) => OptionalValue<string>);
  on_clicked?: (event: FieldmagicDataGridOnRenderedDataClickedEvent) => void;
  is_group_child_count_ref?: boolean;
};

@Component({
  selector: `fieldmagic-data-grid-currency-cell-renderer`,
  template: `
    <fieldmagic-text
      [content]="value | currency"
      [tooltip]="tooltip$ | async"
      [purpose]="purpose$ | async"
      [class.font-weight-bold]="isGroup$ | async"
      (click)="onClicked()"
    >
    </fieldmagic-text>
  `,
})
export class FieldmagicDataGridCurrencyCellRenderer
  implements ICellRendererAngularComp
{
  value?: number;

  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly purpose$ = new BehaviorSubject<FieldmagicTextPurpose>('body');
  readonly isGroup$ = new BehaviorSubject<boolean>(false);

  private readonly _metadata$ = new BehaviorSubject<OptionalValue<FieldmagicDataGridCurrencyRenderingConfig>>(null);
  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellRendererParams>>(null);

  agInit(params: ICellRendererParams): void {
    this.isGroup$.next(params.node.group);

    this.value = to_numeric(
      fallback(params.value, {
        to: () => 0,
      }),
    );

    const metadata: FieldmagicDataGridCurrencyRenderingConfig = fallback(params.colDef.cellRendererParams, {
      to: () => ({}),
    });

    this.tooltip$.next(when(is_callable(metadata.placeholder), {
      then: () => (metadata.placeholder as CallableFunction)(params),
      else: () => metadata.placeholder,
    }));

    this.purpose$.next(when<FieldmagicTextPurpose>(is_callable(metadata.on_clicked), {
      then: () => 'link',
      else: () => this.purpose$.value,
    }));

    this._metadata$.next(metadata);
    this._params$.next(params);
  }

  refresh = (_) => false;

  onClicked(): void {
    const metadata = this._metadata$.value;
    const params = this._params$.value;

    if (blank(metadata)
      || ! is_callable(metadata.on_clicked)
      || blank(params)
    ) {
      return;
    }

    metadata.on_clicked({
      node: params.node,
    });
  }
}

export type FieldmagicDataGridCurrencyRenderingConfig = FieldmagicDataGridRenderingConfig;

@Component({
  selector: 'fieldmagic-data-grid-numeric-cell-renderer',
  template: `
    <span
      [matTooltip]="tooltip$ | async | translate"
      [class.font-weight-bold]="isGroup$ | async"
    >
      {{ value | format_number }}
    </span>
  `,
})
export class FieldmagicDataGridNumericCellRenderer
  implements ICellRendererAngularComp
{
  value?: number;

  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly isGroup$ = new BehaviorSubject<boolean>(false);

  agInit(params: ICellRendererParams): void {
    this.isGroup$.next(params.node.group);

    this.value = to_numeric(
      fallback(params.value, {
        to: () => 0,
      }),
    );

    const metadata: FieldmagicDataGridNumericRenderingConfig = fallback(params.colDef.cellRendererParams, {
      to: () => ({}),
    });

    this.tooltip$.next(when(is_callable(metadata.placeholder), {
      then: () => (metadata.placeholder as CallableFunction)(params),
      else: () => metadata.placeholder,
    }));
  }

  refresh = (_) => false;
}

export type FieldmagicDataGridNumericRenderingConfig = FieldmagicDataGridRenderingConfig;

@Component({
  selector: `fieldmagic-data-grid-actions-cell-renderer`,
  template: `
    <div
      *ngIf="deletable || editable"
      class="d-flex d-flex-gap align-items-center"
    >
      <fieldmagic-primary-button
        *ngIf="editable"
        purpose="icon"
        icon="pencil"
        (click)="onEdit()"
      >
      </fieldmagic-primary-button>

      <fieldmagic-primary-button
        *ngIf="deletable"
        purpose="delete-icon"
        (click)="onDelete()"
      >
      </fieldmagic-primary-button>
    </div>
  `,
})
export class FieldmagicDataGridActionsCellRenderer
  implements ICellRendererAngularComp
{
  editable: boolean = false;
  deletable: boolean = false;

  agInit(params: ICellRendererParams): void {
    const data: OptionalValue<FieldmagicDataGridActionableData> = params.data;

    if (blank(data)) {
      return;
    }

    this.editable = data_is_true(
      fallback(data.editable, {
        to: () => true,
      }),
    );

    this.deletable = data_is_true(
      fallback(data.deletable, {
        to: () => true,
      }),
    );
  }

  refresh(_: any): boolean {
    return false;
  }
}

export type FieldmagicDataGridActionableData = {
  editable?: boolean;
  deletable?: boolean;
};

@Component({
  selector: 'fieldmagic-data-grid-percentage-cell-renderer',
  template: `
    <div class="d-flex d-flex-gap">
      <span
        [class.text-success]="value > 0"
        [class.text-danger]="value < 0"
        [class.font-weight-bold]="isGroup$ | async"
      >
        {{ '%.3f %%' | sprintf : [value] }}
      </span>

      <fieldmagic-icon
        *ngIf="value !== 0"
        [icon]="[
          {
            icon: value > 0 ? 'caret-up' : 'caret-down',
            klass: value > 0 ? 'text-success' : 'text-danger'
          }
        ]"
      >
      </fieldmagic-icon>
    </div>
  `,
})
export class FieldmagicDataGridPercentageCellRenderer
  implements ICellRendererAngularComp
{
  value: number = 0;

  readonly isGroup$ = new BehaviorSubject<boolean>(false);

  agInit(params: ICellRendererParams): void {
    this.isGroup$.next(params.node.group);

    let value = to_numeric(params.value);

    if (params.node.group) {
      value /= params.node.allChildrenCount;
    }

    if (value > 100) {
      value = 100;
    } else if (value < -100) {
      value = -100;
    }

    this.value = value;
  }

  refresh = (_: any): boolean => false;
}

@Component({
  selector: 'fieldamgic-data-grid-progressive-percentage-cell-renderer',
  template: `
    <fieldmagic-pill
      [content]="progress"
      [variant]="variant"
    >
    </fieldmagic-pill>
  `,
})
export class FieldmagicDataGridProgressivePercentageCellRenderer
  implements ICellRendererAngularComp
{
  progress: string = 'not_started';
  variant: FieldmagicPillVariant = 'default';

  constructor(
    private readonly _translator: TranslateService,
  ) {}

  agInit(params: ICellRendererParams): void {
    let value = to_numeric(params.value);

    if (params.node.group) {
      value = arr_reduce(params.node.allLeafChildren, {
        initial_value: 0,
        reducer: (initial, node) => {
          initial += to_numeric(data_get(node.data, params.colDef.field));

          return initial;
        }
      });

      value /= params.node.allChildrenCount;
    }

    if (value > 100) {
      value = 100;
    } else if (value < 0) {
      value = 0;
    }

    if (value < 1) {
      this.progress = 'not_started';
      this.variant = 'default';
    } else if (value > 0 && value < 100) {
      this.progress = spf(this._translator.instant('In Progress (%.2f%%)'), {
        args: [value],
      });
      this.variant = 'info';
    } else if (value === 100) {
      this.progress = 'completed';
      this.variant = 'success';
    }
  }

  refresh = (_: any): boolean => false;
}

@Component({
  selector: 'fieldmagic-data-grid-checkbox-cell-renderer',
  template: `{{ label }}`
})
export class FieldmagicDataGridCheckboxCellRenderer implements ICellRendererAngularComp {
  label?: OptionalValue<string>;

  constructor(
    private readonly _translator: TranslateService,
  ) {}

  agInit(params: ICellRendererParams): void {
    this.label = this._translator.instant(data_is_true(params.value) ? 'yes' : 'no');
  }

  refresh = (_) => false;
}

@Component({
  selector: 'fieldmagic-data-grid-datetime-cell-renderer',
  template: `
    <ng-container *ngIf="value | filled">
      {{ value | format: 'lll' }}
    </ng-container>
  `
})
export class FieldmagicDataGridDateTimeCellRenderer implements ICellRendererAngularComp {
  value?: OptionalValue<string>;

  agInit(params: ICellRendererParams): void {
    if (params.node.group) {
      return;
    }

    this.value = to_local_datetime(params.value);
  }

  refresh = () => false;
}

export type FieldmagicDataGridOnRenderedDataClickedEvent = {
  node: RowNode;
};

@Component({
  selector: 'fieldmagic-data-grid-text-cell-renderer',
  template: `
    <ng-container *ngIf="routing$ | async as routing; else content">
      <a
        [attr.target]="routing.target"
        [routerLink]="routing.paths"
        [queryParams]="routing.queries"
        [matTooltip]="tooltip$ | async"
      >
        <fieldmagic-text
          [content]="value"
        >
        </fieldmagic-text>
      </a>
    </ng-container>

    <ng-template #content>
      <fieldmagic-text
        [content]="value"
        [purpose]="purpose$ | async"
        [matTooltip]="tooltip$ | async"
        (click)="onClicked()"
      >
      </fieldmagic-text>
    </ng-template>
  `,
})
export class FieldmagicDataGridTextCellRendererComponent implements ICellRendererAngularComp {
  value?: OptionalValue<string>;

  readonly purpose$ = new BehaviorSubject<OptionalValue<FieldmagicTextPurpose>>('body');
  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly routing$ = new BehaviorSubject<OptionalValue<FieldmagicDataGridCellRoutingData>>(null);

  private readonly _metadata$ = new BehaviorSubject<OptionalValue<FieldmagicDataGridTextRenderingConfig>>(null);
  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellRendererParams>>(null);

  agInit(params: ICellRendererParams): void {
    this.value = data_nullify(params.value);

    const metadata: FieldmagicDataGridTextRenderingConfig = fallback(params.colDef.cellRendererParams, {
      to: () => ({}),
    });

    this.tooltip$.next(when(is_callable(metadata.placeholder), {
      then: () => (metadata.placeholder as CallableFunction)(params),
      else: () => metadata.placeholder,
    }));

    this.purpose$.next(when<FieldmagicTextPurpose>(is_callable(metadata.on_clicked), {
      then: () => 'link',
      else: () => this.purpose$.value,
    }));

    if (filled(metadata) && is_callable(metadata.routing)) {
      const data = _extract_data_from_rendering_params(params);

      if (filled(data)) {
        this.routing$.next(metadata.routing(params, data));
      }
    }

    this._params$.next(params);
    this._metadata$.next(metadata);
  }

  refresh = (_) => false;

  onClicked(): void {
    const metadata = this._metadata$.value;
    const params = this._params$.value;

    if (blank(metadata)
      || ! is_callable(metadata.on_clicked)
      || blank(params)
    ) {
      return;
    }

    metadata.on_clicked({
      node: params.node,
    });
  }
}

export type FieldmagicDataGridTextRenderingConfig = FieldmagicDataGridRenderingConfig & {
  routing?: FieldmagicDataGridRoutingCallback;
};

@Component({
  selector: 'fieldmagic-data-grid-group-cell-renderer',
  template: `
    <ng-container *ngIf="routing$ | async as routing; else content">
      <a
        [attr.target]="routing.target"
        [routerLink]="routing.paths"
        [queryParams]="routing.queries"
        [matTooltip]="tooltip$ | async"
      >
        <fieldmagic-text
          [content]="content$ | async"
        >
        </fieldmagic-text>
      </a>
    </ng-container>

    <ng-template #content>
      <fieldmagic-text
        [content]="content$ | async"
        [purpose]="purpose$ | async"
        [matTooltip]="tooltip$ | async"
        (click)="onClicked()"
      >
      </fieldmagic-text>
    </ng-template>
  `,
})
export class FieldmagicDataGridGroupCellRendererComponent implements ICellRendererAngularComp {
  readonly purpose$ = new BehaviorSubject<OptionalValue<FieldmagicTextPurpose>>('body');

  readonly content$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly routing$ = new BehaviorSubject<OptionalValue<FieldmagicDataGridCellRoutingData>>(null);

  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);

  private readonly _metadata$ = new BehaviorSubject<OptionalValue<FieldmagicDataGridCurrencyRenderingConfig>>(null);
  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellRendererParams>>(null);

  agInit(params: ICellRendererParams): void {
    let content = spf('%s (%d)', {
      args: [params.node.key, params.node.allLeafChildren.length],
    });

    let metadata: OptionalValue<FieldmagicDataGridGroupCellRenderingConfig> = {
      ... (params.node.leafGroup && fallback(data_get(params.node.rowGroupColumn, 'colDef.cellRendererParams'), {
        to: () => ({}),
      })),
      ... fallback(data_get(params.columnApi.getColumn(params.node.field), 'colDef.cellRendererParams'), {
        to: () => ({}),
      }),
    };

    let groupChildColRef: OptionalValue<ColDef>;

    for (const column of params.columnApi.getAllColumns()) {
      const colParams: FieldmagicDataGridRenderingConfig = fallback(column.getColDef().cellRendererParams, {
        to: () => ({}),
      });

      if (data_is_true(colParams.is_group_child_count_ref)) {
        groupChildColRef = column.getColDef();
        break;
      }
    }

    if (filled(groupChildColRef)) {
      const count = arr_count(arr_filter(params.node.allLeafChildren, {
        predicate: (node) => filled(data_get(node, ['data', groupChildColRef.field])),
      }));

      if (count === 0) {
        content = params.node.key;
      } else {
        content = spf('%s (%d)', {
          args: [params.node.key, count],
        });
      }
    }

    if (filled(metadata) && is_callable(metadata.routing)) {
      this.routing$.next(metadata.routing(params, _extract_data_from_rendering_params(params)));
    }

    this.purpose$.next(when<FieldmagicTextPurpose>(is_callable(metadata.on_clicked), {
      then: () => 'link',
      else: () => this.purpose$.value,
    }));

    this._params$.next(params);
    this._metadata$.next(metadata);

    this.content$.next(content);
  }

  onClicked(): void {
    const metadata = this._metadata$.value;
    const params = this._params$.value;

    if (blank(metadata)
      || ! is_callable(metadata.on_clicked)
      || blank(params)
    ) {
      return;
    }

    metadata.on_clicked({
      node: params.node,
    });
  }

  refresh = () => false;
}

export type FieldmagicDataGridGroupCellRenderingConfig = FieldmagicDataGridRenderingConfig & {
  group_child_count_ref?: OptionalValue<ColDef>;
  routing?: FieldmagicDataGridRoutingCallback;
};

type FieldmagicDataGridRoutingCallback<D = Record<string, any>> = (params: ICellRendererParams, data: OptionalValue<D>) => FieldmagicDataGridCellRoutingData;

type FieldmagicDataGridCellRoutingData = {
  paths: string[];
  queries?: Record<string, string>;
  target?: string;
};

const _extract_data_from_rendering_params = (params: ICellRendererParams): OptionalValue<Record<string, any>> => {
  if (params.node.group) {
    return data_nullify(data_get(arr_first(params.node.allLeafChildren), 'data'));
  }

  return data_nullify(params.node.data);
};